import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

export const slackNotificationTypes = {
	TECHNICAL_ISSUES: "technicalIssues",
	COPY_CONTENT: "copyContent",
};

import api from "src/newApi";
import { queryClient } from "src/queryClient";
import { NotificationStatus } from "../helpers/notificationsTypes";
import options from "./defaultOptions";

export function useGetNotificationCount(enabled = true) {
	const query = useQuery({
		queryKey: ["notifications-count"],

		queryFn: async () => {
			return api.getNotificationsCount();
		},

		...options,
		enabled,
		staleTime: 30 * 1000,
		refetchInterval: 30 * 1000,
	});

	// TanStack Query 5: bez onSuccess w useQuery — czyścimy listę po każdym udanym pobraniu licznika
	useEffect(() => {
		if (query.dataUpdatedAt) {
			queryClient.removeQueries({ queryKey: ["notifications"] });
		}
	}, [query.dataUpdatedAt]);

	return query;
}

export function getCacheSize() {
	try {
		const queries = queryClient.getQueryCache().getAll();
		const bytes = new Blob([JSON.stringify(queries.map((q) => q.state.data))])
			.size;
		return (bytes / 1024 / 1024).toFixed(2);
	} catch {
		return "unknown";
	}
}

if (typeof window !== "undefined") {
	window.getCacheSize = getCacheSize;
}

function getTopCacheEntries(limit = 20) {
	try {
		return queryClient
			.getQueryCache()
			.getAll()
			.map((q) => ({
				queryKey: JSON.stringify(q.queryKey),
				sizeMB: Number(
					(new Blob([JSON.stringify(q.state.data)]).size / 1024 / 1024).toFixed(
						4,
					),
				),
			}))
			.sort((a, b) => b.sizeMB - a.sizeMB)
			.slice(0, limit);
	} catch {
		return [];
	}
}

export function useSendSlackNotification() {
	return useMutation({
		mutationFn: ({ type, data }) => {
			return api.sendSlackNotification(type, {
				...data,
				cacheSizeMB: getCacheSize(),
				queryCount: queryClient.getQueryCache().getAll().length,
				topCacheEntries: getTopCacheEntries(),
			});
		},
	});
}

export function useGetInfiniteNotification(enabled = true) {
	return useInfiniteQuery({
		queryKey: ["notifications"],
		queryFn: async ({ pageParam = 0 }) => {
			const [data] = await api.getUserNotifications({
				skip: pageParam,
				take: 10,
			});

			return data;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.length === 10 ? allPages.length : undefined;
		},
		enabled,
		staleTime: 1 * 60 * 1000,
	});
}

export function useRemoveNotificationSubscription() {
	return useMutation({
		mutationFn: (notification) => {
			return api.removeNotificationSubscription(
				notification.itemId,
				notification.itemType,
			);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.refetchQueries({ queryKey: ["notifications"] }),
				queryClient.refetchQueries({ queryKey: ["notifications-count"] }),
				queryClient.refetchQueries({
					queryKey: ["unresolved-pes-notifications-count"],
				}),
			]);
		},
	});
}

export function useRemoveNotification() {
	return useMutation({
		/** @param {{ id: number, status: string }} notification */
		mutationFn: (notification) => {
			if (notification.status === NotificationStatus.waiting) {
				return api.removeNotification(notification.id);
			}
		},
		onSuccess: async (_data, variables) => {
			if (variables.status === NotificationStatus.waiting) {
				await Promise.all([
					queryClient.refetchQueries({ queryKey: ["notifications"] }),
					queryClient.refetchQueries({ queryKey: ["notifications-count"] }),
					queryClient.refetchQueries({
						queryKey: ["unresolved-pes-notifications-count"],
					}),
				]);
			}
		},
	});
}

export function useRemoveNotificationHard() {
	return useMutation({
		mutationFn: (notification) => {
			return api.removeNotificationHard(notification.id);
		},
		onSuccess: async (_data, _variables) => {
			await Promise.all([
				queryClient.refetchQueries({ queryKey: ["notifications"] }),
				queryClient.refetchQueries({ queryKey: ["notifications-count"] }),
				queryClient.refetchQueries({
					queryKey: ["unresolved-pes-notifications-count"],
				}),
			]);
		},
	});
}

export function useGetCountOfUnresolvedComments() {
	return useQuery({
		queryKey: ["unresolved-pes-notifications-count"],

		queryFn: async () => {
			return api.getCountOfUnresolvedComments();
		},

		...options,
		staleTime: 60 * 1000,
	});
}

export function useGlobalActions() {
	return useMutation({
		mutationFn: ({ action }) => {
			return api.notificationGlobalAction(action);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.refetchQueries({ queryKey: ["notifications"] }),
				queryClient.refetchQueries({ queryKey: ["notifications-count"] }),
				queryClient.refetchQueries({
					queryKey: ["unresolved-pes-notifications-count"],
				}),
			]);
		},
	});
}
